import * as GL from './webgl2constants'

export function typeIsTexture(type: GLenum): boolean {
	switch (type) {
		case GL.SAMPLER_2D:
		case GL.SAMPLER_3D:
		case GL.SAMPLER_CUBE:
		case GL.SAMPLER_CUBE_SHADOW:
		case GL.SAMPLER_2D_SHADOW:
		case GL.SAMPLER_2D_ARRAY:
		case GL.SAMPLER_2D_ARRAY_SHADOW:
		case GL.INT_SAMPLER_2D:
		case GL.INT_SAMPLER_3D:
		case GL.INT_SAMPLER_CUBE:
		case GL.INT_SAMPLER_2D_ARRAY:
		case GL.UNSIGNED_INT_SAMPLER_2D:
		case GL.UNSIGNED_INT_SAMPLER_3D:
		case GL.UNSIGNED_INT_SAMPLER_CUBE:
		case GL.UNSIGNED_INT_SAMPLER_2D_ARRAY:
			return true
		default: return false
	}
}


// --- interpolation mode ---

export enum InterpolationMode {
	nearest = "nearest",
	linear = "linear",
}
export enum MipmapInterpolationMode {
	nearest = "nearest",
	linear = "linear",
	none = "none",
}
export function glInterpMode(interpolationMode: InterpolationMode, mipmapInterpolationMode: MipmapInterpolationMode): GLenum {
	switch (interpolationMode) {
		case InterpolationMode.nearest:
			switch (mipmapInterpolationMode) {
				case MipmapInterpolationMode.none: return GL.NEAREST
				case MipmapInterpolationMode.nearest: return GL.NEAREST_MIPMAP_NEAREST
				case MipmapInterpolationMode.linear: return GL.NEAREST_MIPMAP_LINEAR
				default: throw `error : mipmapInterpolationMode must be "${Object.keys(MipmapInterpolationMode).join(`" or "`)} but is "${mipmapInterpolationMode}""`
			}
		case InterpolationMode.linear:
			switch (mipmapInterpolationMode) {
				case MipmapInterpolationMode.none: return GL.LINEAR
				case MipmapInterpolationMode.nearest: return GL.LINEAR_MIPMAP_NEAREST
				case MipmapInterpolationMode.linear: return GL.LINEAR_MIPMAP_LINEAR
				default: throw `error : mipmapInterpolationMode must be "${Object.keys(MipmapInterpolationMode).join(`" or "`)} but is "${mipmapInterpolationMode}""`
			}
		default: throw `error : interpolationMode must be "${Object.keys(InterpolationMode).join(`" or "`)}" but is "${interpolationMode}"`
	}
}

// --- wrap mode ---

export enum WrapMode {
	repeat = "repeat",
	clamp = "clamp",
	mirror = "mirror"
}
export function glWrapMode(mode: WrapMode) {
	switch (mode) {
		case WrapMode.repeat: return GL.REPEAT
		case WrapMode.clamp: return GL.CLAMP_TO_EDGE
		case WrapMode.mirror: return GL.MIRRORED_REPEAT
	}
}

// --- texture format ---

export enum TexFormat {
	r = "r",
	rg = "rg",
	rgb = "rgb",
	rgba = "rgba",
	d = "d", // depth
	ds = "ds", // depth + stencil
	a = "a", // alpha
	l = "l", // luminance
	la = "la", // luminance + alpha
}

export function texFormatDim(texFormat: TexFormat): number {
	switch (texFormat) {
		case TexFormat.r: return 1
		case TexFormat.rg: return 2
		case TexFormat.rgb: return 3
		case TexFormat.rgba: return 4
		case TexFormat.d: return 1
		case TexFormat.a: return 1
		case TexFormat.ds: return 2
		case TexFormat.la: return 2
		case TexFormat.l: return 1
	}
}

// tis table is really useful ! the kronos documentation looks very messy compared to this : https://webgl2fundamentals.org/webgl/lessons/webgl-data-textures.html
// TODO make custom special textures for other formats
// TODO enable other types when tested
/** returns [internalFormat: GLenum, format: GLenum] */
export function glTexFormat(format: TexFormat, type: GLenum): [GLenum, GLenum] {
	switch (format) {
		case TexFormat.r:
			switch (type) {
				// case GL.BYTE:           return [GL.R8I   , GL.RED_INTEGER]
				// case GL.SHORT:          return [GL.R16I  , GL.RED_INTEGER]
				// case GL.INT:            return [GL.R32I  , GL.RED_INTEGER]
				// case GL.UNSIGNED_BYTE:  return [GL.R8UI  , GL.RED_INTEGER] // internalFormat could be GL.R8
				// case GL.UNSIGNED_SHORT: return [GL.R16UI , GL.RED_INTEGER]
				// case GL.UNSIGNED_INT:   return [GL.R32UI , GL.RED_INTEGER]
				// case GL.HALF_FLOAT:     return [GL.R16F  , GL.RED]
				case GL.FLOAT: return [GL.R32F, GL.RED] // internalFormat could be GL.R16F
			} break
		case TexFormat.rg:
			switch (type) {
				// case GL.BYTE:           return [GL.RG8I   , GL.RG_INTEGER]
				// case GL.SHORT:          return [GL.RG16I  , GL.RG_INTEGER]
				// case GL.INT:            return [GL.RG32I  , GL.RG_INTEGER]
				// case GL.UNSIGNED_BYTE:  return [GL.RG8UI  , GL.RG_INTEGER] // internalFormat could be GL.RG8
				// case GL.UNSIGNED_SHORT: return [GL.RG16UI , GL.RG_INTEGER]
				// case GL.UNSIGNED_INT:   return [GL.RG32UI , GL.RG_INTEGER]
				// case GL.HALF_FLOAT:     return [GL.RG16F  , GL.RG]
				case GL.FLOAT: return [GL.RG32F, GL.RG] // internalFormat could be GL.RG16F
			} break
		case TexFormat.rgb:
			switch (type) {
				// case GL.BYTE:           return [GL.RGB8I   , GL.RGB_INTEGER]
				// case GL.SHORT:          return [GL.RGB16I  , GL.RGB_INTEGER]
				// case GL.INT:            return [GL.RGB32I  , GL.RGB_INTEGER]
				// case GL.UNSIGNED_BYTE:  return [GL.RGB8UI  , GL.RGB_INTEGER] // internalFormat could be GL.RGB|GL.RGB8|GL.SRGB8|GL.RGB565
				// case GL.UNSIGNED_SHORT: return [GL.RGB16UI , GL.RGB_INTEGER]
				// case GL.UNSIGNED_INT:   return [GL.RGB32UI , GL.RGB_INTEGER]
				// case GL.HALF_FLOAT:     return [GL.RGB16F  , GL.RGB] // internalFormat could be GL.RGB9_E5|GL.R11F_G11F_B10F 
				case GL.FLOAT: return [GL.RGB32F, GL.RGB] // internalFormat could be GL.RGB9_E5|GL.R11F_G11F_B10F|GL.RGB16F
				// case GL.UNSIGNED_INT_10F_11F_11F_REV: return [GL.R11F_G11F_B10F, GL.RGB]
				// case GL.UNSIGNED_SHORT_5_6_5: return [GL.RGB565, GL.RGB] // internalFormat could be GL.RGB
			} break
		case TexFormat.rgba:
			switch (type) {
				// case GL.BYTE:           return [GL.RGBA8I   , GL.RGBA_INTEGER] // internalFormat could be GL.RGBA8_SNORM
				// case GL.SHORT:          return [GL.RGBA16I  , GL.RGBA_INTEGER]
				// case GL.INT:            return [GL.RGBA32I  , GL.RGBA_INTEGER]
				// case GL.UNSIGNED_BYTE:  return [GL.RGBA8UI  , GL.RGBA_INTEGER] // internalFormat could be GL.RGBA|GL.RGB5_A1|GL.RGBA4|GL.RGBA8|GL.SRGB8_ALPHA8
				// case GL.UNSIGNED_SHORT: return [GL.RGBA16UI , GL.RGBA_INTEGER]
				// case GL.UNSIGNED_INT:   return [GL.RGBA32UI , GL.RGBA_INTEGER]
				// case GL.HALF_FLOAT:     return [GL.RGBA16F  , GL.RGBA]
				case GL.FLOAT: return [GL.RGBA32F, GL.RGBA] // internalFormat could be GL.RGBA16F
				// case GL.UNSIGNED_INT_2_10_10_10_REV: return [GL.RGB10_A2, GL.RGBA] // or [GL.RGB10_A2UI, GL.RGBA_INTEGER]
				// case GL.UNSIGNED_SHORT_5_5_5_1: return [GL.RGB5_A1, GL.RGBA] // internalFormat could be GL.RGBA
				// case GL.UNSIGNED_SHORT_4_4_4_4: return [GL.RGBA4, GL.RGBA]   // internalFormat could be GL.RGBA
			} break
		case TexFormat.d:
			switch (type) {
				case GL.FLOAT: return [GL.DEPTH_COMPONENT32F, GL.DEPTH_COMPONENT]
				// case GL.UNSIGNED_INT:   return [GL.DEPTH_COMPONENT24  , GL.DEPTH_COMPONENT] // internalFormat could be GL.DEPTH_COMPONENT16
				// case GL.UNSIGNED_SHORT: return [GL.DEPTH_COMPONENT16  , GL.DEPTH_COMPONENT]
			} break
		case TexFormat.ds:
			switch (type) {
				// case GL.UNSIGNED_INT_24_8:              return [GL.DEPTH24_STENCIL8  , GL.DEPTH_STENCIL]
				// case GL.FLOAT_32_UNSIGNED_INT_24_8_REV: return [GL.DEPTH32F_STENCIL8 , GL.DEPTH_STENCIL]
			} break
		case TexFormat.a:
			switch (type) {
				// case GL.UNSIGNED_BYTE: return [GL.ALPHA, GL.ALPHA]
			} break
		case TexFormat.l:
			switch (type) {
				// case GL.UNSIGNED_BYTE: return [GL.LUMINANCE, GL.LUMINANCE]
			} break
		case TexFormat.la:
			switch (type) {
				// case GL.UNSIGNED_BYTE: return [GL.LUMINANCE_ALPHA, GL.LUMINANCE_ALPHA]
			} break
		default: throw `format must be "${Object.keys(TexFormat).join(`" or "`)} but is "${format}""`
	}
	throw `format "${format}" doesn't support ${type}`
}
